import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useClipboard } from '../hooks/useClipboard';
import DropdownMenu, { type DropdownItem } from '../components/DropdownMenu';
import ConfirmacaoModal from '../components/ConfirmacaoModal';
import EnviarEmailModal from '../components/EnviarEmailModal';
import EventosNfeModal from '../components/EventosNfeModal';
import JsonViewerModal from '../components/JsonViewerModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Radio,
  Truck,
  BarChart3,
  Users as UsersIcon,
  FileSpreadsheet,
  Copy,
  Check,
  MoreHorizontal,
  KeyRound,
  Hash,
  Braces,
  FileEdit,
  Clock,
  PackageOpen,
  AlertOctagon,
  MessageCircle,
  Download,
  FileText,
  Mail,
  Ban,
  Pencil,
  Upload,
  Search as SearchIcon,
  AlertTriangle,
  Send,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Filter
} from 'lucide-react';
import {
  CTe,
  NFe,
  TipoDoc,
  duplicarCTe,
  duplicarNFe,
  duplicarCteOS,
  getContadoresDocumentos,
  listCTeOS,
  listCTes,
  listMDFe,
  listNFes
} from '../api/documentos';
import { getEmitente } from '../api/emitente';
import { getCertificado } from '../api/certificado';
import { formatDate, formatMoney } from '../utils/formatters';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import EmpresaHeader from '../components/EmpresaHeader';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';

const TIPOS: { id: TipoDoc; label: string; dot: string }[] = [
  { id: 'cte', label: 'CT-e', dot: 'bg-emerald-400' },
  { id: 'nfe', label: 'NF-e', dot: 'bg-indigo-400' },
  { id: 'cte-os', label: 'CT-e OS', dot: 'bg-violet-400' },
  { id: 'mdfe', label: 'MDF-e', dot: 'bg-amber-400' }
];

type DocEmitido = NFe | CTe;

const PAGE_SIZE = 20;

const STATUS_OPTS: { value: string; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'autorizada', label: 'Autorizadas' },
  { value: 'cancelada', label: 'Canceladas' },
  { value: 'rejeitada', label: 'Rejeitadas' },
  { value: 'denegada', label: 'Denegadas' },
  { value: 'assinada', label: 'Assinadas' },
  { value: 'transmitida', label: 'Transmitidas' }
];

function valorDoc(d: DocEmitido): number {
  if ('valor_prestacao' in d) return Number(d.valor_prestacao || 0);
  return Number(d.valor_total || 0);
}
function rotaDoc(d: DocEmitido): string | null {
  if ('uf_origem' in d) return `${d.uf_origem || '-'} → ${d.uf_destino || '-'}`;
  if ('natureza_operacao' in d) return d.natureza_operacao || null;
  return null;
}

function chaveEmiss(d: DocEmitido): string {
  return (d.chave || '').toString();
}

export default function EmpresaPainelPage() {
  const { id, tipo } = useParams();
  const chaveCopy = useClipboard();
  const [cceOpen, setCceOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [eventosOpen, setEventosOpen] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const emitenteId = Number(id);
  const tipoParam = (tipo as TipoDoc) || 'cte';
  const { setEmitenteId } = useEmitenteAtivo();

  useEffect(() => {
    if (!isNaN(emitenteId)) setEmitenteId(emitenteId);
  }, [emitenteId, setEmitenteId]);

  const [selId, setSelId] = useState<number | null>(null);
  const [statusFiltro, setStatusFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [dataIni, setDataIni] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [pagina, setPagina] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);

  const empresaQ = useQuery({
    enabled: !isNaN(emitenteId),
    queryKey: ['emitente', emitenteId],
    queryFn: () => getEmitente(emitenteId)
  });
  const certQ = useQuery({
    enabled: !isNaN(emitenteId),
    queryKey: ['emitente-cert', emitenteId],
    queryFn: () => getCertificado(emitenteId)
  });
  const contQ = useQuery({
    enabled: !isNaN(emitenteId),
    queryKey: ['contadores-documentos', emitenteId],
    queryFn: () => getContadoresDocumentos(emitenteId)
  });

  // Filtra TIPOS pelo perfil do emitente (campos emite_*) e redireciona pra primeira habilitada
  const tiposHabilitados = useMemo(() => {
    const emp = empresaQ.data;
    if (!emp) return TIPOS;
    const flagPara: Record<TipoDoc, keyof typeof emp> = {
      nfe: 'emite_nfe',
      cte: 'emite_cte',
      'cte-os': 'emite_ctos',
      mdfe: 'emite_mdfe'
    };
    const filtrados = TIPOS.filter((t) => emp[flagPara[t.id]] !== false);
    return filtrados.length > 0 ? filtrados : TIPOS;
  }, [empresaQ.data]);

  useEffect(() => {
    if (!empresaQ.data || isNaN(emitenteId)) return;
    if (!tiposHabilitados.find((t) => t.id === tipoParam)) {
      navigate(`/empresa/${emitenteId}/painel/${tiposHabilitados[0].id}`, { replace: true });
    }
  }, [empresaQ.data, emitenteId, tipoParam, tiposHabilitados, navigate]);
  // Rascunhos pendentes do tipo atual
  const rascQ = useQuery<Array<{ id: number; numero_proposto: number | null; serie: number | null; motivo: string | null; created_at: string | null; status: string }>>({
    queryKey: ['rascunhos-painel', tipoParam, emitenteId],
    queryFn: async () => {
      if (!emitenteId) return [];
      const tipoApi = tipoParam === 'cte-os' ? 'cte-os' : tipoParam;
      const r = await fetch(`/api/${tipoApi}/rascunhos?emitente_id=${emitenteId}`);
      if (!r.ok) return [];
      return await r.json();
    },
    enabled: !!emitenteId,
  });

  const emitQ = useQuery<DocEmitido[]>({
    enabled: !isNaN(emitenteId),
    queryKey: ['documentos-emitidos', tipoParam, emitenteId],
    queryFn: async () => {
      if (tipoParam === 'nfe') return (await listNFes(emitenteId)) as DocEmitido[];
      if (tipoParam === 'cte') return (await listCTes(emitenteId)) as DocEmitido[];
      if (tipoParam === 'cte-os') return (await listCTeOS(emitenteId)) as DocEmitido[];
      if (tipoParam === 'mdfe') return (await listMDFe(emitenteId)) as DocEmitido[];
      return [];
    }
  });

  const duplicarMut = useMutation({
    mutationFn: (docId: number) => (tipoParam === 'nfe' ? duplicarNFe(docId) : tipoParam === 'cte-os' ? duplicarCteOS(docId) : duplicarCTe(docId)),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['rascunhos', tipoParam] });
      qc.invalidateQueries({ queryKey: ['contadores-documentos'] });
      navigate(`/documentos/${tipoParam}/${r.id}?duplicar=true`);
    }
  });

  const filtrados = useMemo(() => {
    const base = emitQ.data ?? [];
    const buscaLower = busca.trim().toLowerCase();
    const ini = dataIni ? new Date(dataIni + 'T00:00:00') : null;
    const fim = dataFim ? new Date(dataFim + 'T23:59:59') : null;
    return base.filter((d) => {
      if (statusFiltro && d.status !== statusFiltro) return false;
      if (buscaLower) {
        const matches =
          (chaveEmiss(d)).toLowerCase().includes(buscaLower) ||
          String(d.numero).includes(buscaLower) ||
          (d.protocolo ?? '').toLowerCase().includes(buscaLower) ||
          ('natureza_operacao' in d && (d.natureza_operacao ?? '').toLowerCase().includes(buscaLower));
        if (!matches) return false;
      }
      if ((ini || fim) && d.data_emissao) {
        const de = new Date(d.data_emissao);
        if (ini && de < ini) return false;
        if (fim && de > fim) return false;
      }
      return true;
    });
  }, [emitQ.data, busca, statusFiltro, dataIni, dataFim]);

  const totalPags = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const pg = Math.min(pagina, totalPags - 1);
  const slice = filtrados.slice(pg * PAGE_SIZE, (pg + 1) * PAGE_SIZE);

  const selecionado = filtrados.find((d) => d.id === selId) || null;
  const tipoLabel = TIPOS.find((t) => t.id === tipoParam)?.label ?? 'Documento';

  const baixarUrl = (path: 'xml' | 'danfe' | 'dacte', doc: DocEmitido) => {
    window.location.href = `/api/${tipoParam}/${doc.id}/${path}`;
  };

  function avisoAcao(msg: string) {
    setAviso(msg);
    setTimeout(() => setAviso(null), 4000);
  }

  function limparFiltros() {
    setBusca('');
    setStatusFiltro('');
    setDataIni('');
    setDataFim('');
    setPagina(0);
  }

  const isLoading = empresaQ.isLoading || emitQ.isLoading;

  if (!isNaN(emitenteId) && !empresaQ.data && !empresaQ.isLoading) {
    return (
      <div className="card-surface p-6 text-center">
        <p>Empresa não encontrada.</p>
        <Link to="/emitentes" className="btn-primary mt-4 inline-flex">Voltar</Link>
      </div>
    );
  }

  const cont = contQ.data;
  const certDias = certQ.data?.dias_restantes ?? null;
  const certSit = certQ.data?.situacao;

  return (
    <div className="space-y-5 animate-fade-in">
      {/* HEADER */}
      {empresaQ.data && (
        <EmpresaHeader
          emitente={empresaQ.data}
          backTo={`/empresa/${emitenteId}`}
          rightSlot={
            <Link
              to={`/empresa/${emitenteId}`}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" /> Hub
            </Link>
          }
        />
      )}

      {/* TAB DE TIPOS */}
      <div className="sticky top-14 z-30 -mx-3 flex items-center gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50/95 px-3 py-2 backdrop-blur-xl dark:border-slate-800/80 dark:bg-[#080c14]/95 md:-mx-8 md:px-8">
        {tiposHabilitados.map((t) => {
          const isAtiva = t.id === tipoParam;
          const n = cont ? cont.emitidos[t.id === 'cte-os' ? 'cte_os' : t.id] : null;
          return (
            <button
              key={t.id}
              onClick={() => navigate(`/empresa/${emitenteId}/painel/${t.id}`)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 transition ${
                isAtiva
                  ? 'border border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'border border-slate-200 bg-white/80 text-slate-600 hover:text-slate-950 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-900'
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${t.dot}`} />
              {t.label}
              {n != null && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 tab-nums ${isAtiva ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2 pr-2 text-xs text-slate-400">
          {certDias != null && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 border text-[11px] ${
              certSit === 'vencido' ? 'border-rose-200 text-rose-800 bg-rose-50 dark:border-rose-500/30 dark:text-rose-300 dark:bg-rose-500/10' :
              certSit === 'critico' ? 'border-orange-200 text-orange-800 bg-orange-50 dark:border-orange-500/30 dark:text-orange-300 dark:bg-orange-500/10' :
              certSit === 'aviso' ? 'border-amber-200 text-amber-800 bg-amber-50 dark:border-amber-500/30 dark:text-amber-300 dark:bg-amber-500/10' :
              'border-emerald-200 text-emerald-800 bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-300 dark:bg-emerald-500/10'
            }`}>
              Certificado: {certSit === 'vencido' ? 'vencido' : `${certDias} dias`}
            </span>
          )}
        </div>
      </div>

      {/* FILTRO RÁPIDO */}
      <section className="card-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-sky-500/10 border border-sky-500/30 grid place-items-center">
            <Filter className="h-4 w-4 text-sky-700 dark:text-sky-400" />
          </div>
          <div className="flex-1">
            <div className="eyebrow">Filtro rápido</div>
            <h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Buscar nos {tipoLabel} emitidos</h3>
          </div>
          <div className="text-xs text-slate-400 tab-nums">
            {filtrados.length} / {emitQ.data?.length ?? 0}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative md:col-span-2">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500 pointer-events-none" />
            <input
              type="search"
              value={busca}
              onChange={(e) => { setBusca(e.target.value); setPagina(0); }}
              placeholder="Número, chave, valor, status..."
              className="field-input pl-9"
            />
          </div>
          <select
            value={statusFiltro}
            onChange={(e) => { setStatusFiltro(e.target.value); setPagina(0); }}
            className="field-input"
          >
            {STATUS_OPTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={dataIni}
              onChange={(e) => { setDataIni(e.target.value); setPagina(0); }}
              className="field-input"
            />
            <input
              type="date"
              value={dataFim}
              onChange={(e) => { setDataFim(e.target.value); setPagina(0); }}
              className="field-input"
            />
          </div>
        </div>
        {(busca || statusFiltro || dataIni || dataFim) && (
          <button onClick={limparFiltros} className="mt-3 text-xs font-semibold text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300">
            Limpar filtros
          </button>
        )}
      </section>

      {/* AÇÕES */}
      <section className="card-surface p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-violet-500/10 border border-violet-500/30 grid place-items-center">
              <Send className="h-4 w-4 text-violet-400" />
            </div>
            <div>
              <div className="eyebrow">Ações</div>
              <h3 className="text-sm font-semibold text-slate-950 dark:text-white">
                {selecionado ? `${tipoLabel} #${selecionado.numero}/${selecionado.serie ?? 1} selecionado` : `Selecione um ${tipoLabel} na tabela`}
              </h3>
            </div>
          </div>
          {selecionado && (
            <button onClick={() => setSelId(null)} className="text-xs text-neutral-500 hover:text-slate-950 dark:text-neutral-400 dark:hover:text-white">
              Limpar seleção
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Link to={`/documentos/${tipoParam}/novo`} className="btn-primary text-xs !py-1.5">
            <Plus className="h-3.5 w-3.5" /> Emitir {tipoLabel}
          </Link>
          <button
            onClick={async () => {
              if (!emitenteId) { avisoAcao('Selecione um emitente.'); return; }
              avisoAcao('Consultando status SEFAZ...');
              try {
                const r = await fetch(`/api/${tipoParam}/status-servico`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emitente_id: emitenteId }) });
                if (r.status === 404) { avisoAcao(`Consulta SEFAZ de ${tipoLabel} ainda não disponível (em breve).`); return; }
                const data = await r.json();
                if (data.erro || data.error) { avisoAcao(`SEFAZ: ${data.erro || data.error}`); return; }
                const emoji = data.online ? '🟢' : '🔴';
                avisoAcao(`${emoji} SEFAZ ${tipoLabel}: cStat ${data.codigo ?? '?'} · ${data.motivo ?? ''} · ${data.autorizador ?? 'SVRS'} · ${data.ambiente ?? ''}`);
              } catch (e) { avisoAcao('Erro consultar SEFAZ: ' + String(e)); }
            }}
            className="btn-amber text-xs !py-1.5"
            title="Consulta status do servico SEFAZ (disponibilidade)"
          >
            <Radio className="h-3.5 w-3.5" /> SEFAZ
          </button>
          <Link to={`/veiculos?emitente=${emitenteId}`} className="toolbar-btn">
            <Truck className="h-3.5 w-3.5" /> Veículos
          </Link>
          <button
            onClick={() => {
              if (!emitenteId) { avisoAcao('Selecione um emitente.'); return; }
              const ini = dataIni ? `ini=${dataIni}` : '';
              const fim = dataFim ? `fim=${dataFim}` : '';
              const qs = [ini, fim].filter(Boolean).join('&');
              window.location.href = `/api/emitentes/${emitenteId}/relatorio.pdf${qs ? '?' + qs : ''}`;
            }}
            className="toolbar-btn"
            title="Relatório PDF fiscal (faturados + cancelados) — respeita filtro de data"
          >
            <BarChart3 className="h-3.5 w-3.5" /> Relatório
          </button>
          <button
            onClick={() => {
              const rows = filtrados;
              if (rows.length === 0) { avisoAcao('Sem documentos para exportar.'); return; }
              const header = ['Numero', 'Serie', 'Chave', 'Emissao', 'Rota/Natureza', 'Valor', 'Status', 'Protocolo'];
              const csv = [header.join(';')].concat(rows.map(d => [
                d.numero, d.serie ?? 1, d.chave ?? '', d.data_emissao ?? '',
                (rotaDoc(d) ?? '').replace(/;/g,','),
                String(valorDoc(d)).replace('.',','),
                d.status ?? '', d.protocolo ?? ''
              ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(';'))).join('\n');
              const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `${tipoParam}_${empresaQ.data?.cnpj || 'export'}_${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
              URL.revokeObjectURL(a.href);
            }}
            className="toolbar-btn"
            title="Exporta a lista filtrada para CSV"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> CSV
          </button>
          {(tipoParam === 'cte' || tipoParam === 'nfe' || tipoParam === 'cte-os') && (
            <button
              disabled={!selecionado || duplicarMut.isPending}
              onClick={() => selecionado && duplicarMut.mutate(selecionado.id)}
              className="btn-primary text-xs !py-1.5"
              title={`Cria um novo rascunho a partir desta ${tipoLabel}`}
            >
              <Copy className="h-3.5 w-3.5" /> Duplicar
            </button>
          )}
          <button
            disabled={!selecionado}
            onClick={() => selecionado && baixarUrl(tipoParam === 'nfe' ? 'danfe' : 'dacte', selecionado)}
            className="btn-sky text-xs !py-1.5"
            title={selecionado?.status === 'cancelada' ? 'Baixar PDF — comprovante de cancelamento (documento cancelado)' : 'Baixar PDF do documento'}
          >
            <Download className="h-3.5 w-3.5" /> PDF
          </button>
          <button
            disabled={!selecionado}
            onClick={() => selecionado && baixarUrl('xml', selecionado)}
            className="toolbar-btn"
            title={selecionado?.status === 'cancelada' ? 'Baixar XML — evento de cancelamento (documento cancelado)' : 'Baixar XML do documento'}
          >
            <FileText className="h-3.5 w-3.5" /> XML
          </button>
          <button
            disabled={!selecionado}
            onClick={() => selecionado && setEmailOpen(true)}
            className="btn-amber text-xs !py-1.5"
            title="Envia XML + PDF por email — já vem com o e-mail do cadastro preenchido"
          >
            <Mail className="h-3.5 w-3.5" /> Email
          </button>
          <button
            disabled={!selecionado}
            onClick={async () => {
              if (!selecionado) return;
              const jus = prompt('Justificativa do cancelamento (mín. 15 caracteres):', 'Cancelamento por emissao indevida');
              if (!jus) return;
              if (jus.trim().length < 15) { avisoAcao('Justificativa precisa ter pelo menos 15 caracteres.'); return; }
              try {
                const r = await fetch(`/api/${tipoParam}/${selecionado.id}/cancelar`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ justificativa: jus }),
                });
                const data = await r.json();
                if (data.sucesso) {
                  avisoAcao(`Cancelado (cStat ${data.cStat}): ${data.xMotivo}`);
                  emitQ.refetch();
                  setSelId(null);
                } else {
                  avisoAcao(`Falhou: ${data.xMotivo || data.error || 'erro'}`);
                }
              } catch (e) {
                avisoAcao('Erro ao cancelar: ' + String(e));
              }
            }}
            className="btn-danger text-xs !py-1.5"
            title='Envia evento 110111 à SEFAZ'
          >
            <Ban className="h-3.5 w-3.5" /> Cancelar
          </button>
          {tipoParam === 'nfe' && (
            <Link
              to={
                selecionado && (selecionado.status === 'rejeitada' || selecionado.status === 'denegada' || selecionado.status === 'erro') && selecionado.numero
                  ? `/inutilizacoes?numero=${selecionado.numero}&serie=${selecionado.serie ?? 1}`
                  : '/inutilizacoes'
              }
              className={`toolbar-btn ${
                selecionado && (selecionado.status === 'rejeitada' || selecionado.status === 'denegada' || selecionado.status === 'erro')
                  ? 'text-amber-800 border-amber-200 bg-amber-50 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-500/40 dark:bg-amber-500/10 dark:hover:bg-amber-500/20'
                  : ''
              }`}
              title={
                selecionado
                  ? `Inutilizar numeração ${selecionado.numero ?? ''} (SEFAZ NfeInutilizacao4)`
                  : 'Abrir gerenciador de inutilizações'
              }
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {selecionado && (selecionado.status === 'rejeitada' || selecionado.status === 'denegada' || selecionado.status === 'erro') && selecionado.numero
                ? `Inutilizar nº ${selecionado.numero}`
                : 'Inutilizar'}
            </Link>
          )}
          {tipoParam === 'mdfe' && (
            <button
              disabled={!selecionado || selecionado.status !== 'autorizada'}
              onClick={async () => {
                if (!selecionado) return;
                if (!confirm(`Encerrar MDF-e #${selecionado.numero}?\n\nApós encerrado, não é mais possível cancelar.`)) return;
                avisoAcao('Encerrando...');
                try {
                  const r = await fetch(`/api/mdfe/${selecionado.id}/encerrar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                  const data = await r.json();
                  if (data.sucesso) { avisoAcao(`Encerrado (cStat ${data.cStat}): ${data.xMotivo}`); emitQ.refetch(); }
                  else avisoAcao(`Falhou: ${data.xMotivo || data.error}`);
                } catch (e) { avisoAcao('Erro: ' + String(e)); }
              }}
              className="btn-amber text-xs !py-1.5"
              title="Envia evento 110112 (encerramento) à SEFAZ — obrigatório após a viagem"
            >
              <Ban className="h-3.5 w-3.5" /> Encerrar
            </button>
          )}
          <Link
            to={selecionado ? `/documentos/${tipoParam}/${selecionado.id}` : '#'}
            className={`toolbar-btn ${!selecionado ? 'opacity-40 pointer-events-none' : ''}`}
          >
            <Pencil className="h-3.5 w-3.5" /> Editar
          </Link>
          <label className="toolbar-btn cursor-pointer" title="Importa XML de CT-e já autorizado na SEFAZ (emitido em outro sistema)">
            <Upload className="h-3.5 w-3.5" /> Upload XML
            <input
              type="file"
              accept=".xml"
              className="hidden"
              onChange={async (ev) => {
                const f = ev.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('file', f);
                avisoAcao(`Importando ${f.name}...`);
                try {
                  const r = await fetch(`/api/${tipoParam === 'cte-os' ? 'cte-os' : 'cte'}/upload-xml`, { method: 'POST', body: fd });
                  const data = await r.json();
                  if (data.sucesso) {
                    avisoAcao(`XML ${data.acao}: CT-e #${data.numero} · chave ...${String(data.chave).slice(-8)}`);
                    emitQ.refetch();
                  } else avisoAcao(`Falhou: ${data.error}`);
                } catch (e) { avisoAcao('Erro: ' + String(e)); }
                ev.target.value = '';
              }}
            />
          </label>
          <button
            disabled={!selecionado}
            onClick={async () => {
              if (!selecionado) return;
              avisoAcao('Consultando SEFAZ...');
              try {
                const r = await fetch(`/api/${tipoParam}/${selecionado.id}/consultar-sefaz`, { method: 'POST' });
                const data = await r.json();
                if (data.erro || data.error) { avisoAcao(`SEFAZ: ${data.erro || data.error}`); return; }
                const recon = Array.isArray(data.reconciliado) && data.reconciliado.length > 0
                  ? ` · ✅ reconciliado: ${data.reconciliado.join(', ')}` : '';
                avisoAcao(`cStat ${data.cStat}: ${data.xMotivo}${data.nProt ? ' · prot ' + data.nProt : ''}${recon}`);
                if (Array.isArray(data.reconciliado) && data.reconciliado.length > 0) {
                  emitQ.refetch();
                }
              } catch (e) { avisoAcao('Erro: ' + String(e)); }
            }}
            className="toolbar-btn"
            title="Consulta status SEFAZ do documento selecionado (read-only)"
          >
            <SearchIcon className="h-3.5 w-3.5" /> Consultar
          </button>
          <button
            disabled={!selecionado}
            onClick={() => {
              if (!selecionado) return;
              const d = selecionado as DocEmitido & { c_stat_sefaz?: string; x_motivo_sefaz?: string; motivo?: string; data_emissao?: string; data_autorizacao?: string };
              const info = {
                numero_serie: `${d.numero}/${d.serie ?? 1}`,
                chave: d.chave || '(sem chave)',
                status: d.status,
                protocolo: d.protocolo || '(sem protocolo)',
                cStat_SEFAZ: d.c_stat_sefaz || '—',
                xMotivo_SEFAZ: d.x_motivo_sefaz || d.motivo || '—',
                emissao: d.data_emissao,
                autorizacao: d.data_autorizacao || '—',
                valor: valorDoc(d),
                rota_natureza: rotaDoc(d),
              };
              const texto = Object.entries(info).map(([k, v]) => `${k}: ${v}`).join('\n');
              alert('Detalhes do documento\n━━━━━━━━━━━━━━━━━━\n' + texto);
            }}
            className="toolbar-btn"
            title="Mostra cStat + xMotivo da SEFAZ e dados completos do documento"
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Erro/JSON
          </button>
          <DropdownMenu
            label="Mais"
            icon={<MoreHorizontal className="h-3.5 w-3.5" />}
            disabled={!selecionado}
            items={selecionado ? (() => {
              const resposta = (selecionado as DocEmitido & { retorno_json?: unknown; json_completo?: unknown }).retorno_json
                ?? (selecionado as DocEmitido & { retorno_json?: unknown; json_completo?: unknown }).json_completo
                ?? null;
              const items: DropdownItem[] = [
                ...(tipoParam === 'nfe' && selecionado.status === 'autorizada' ? [
                  {
                    label: 'Devolver (gerar rascunho)',
                    icon: <PackageOpen className="h-3.5 w-3.5" />,
                    hint: 'Cria rascunho de NF-e de devolução (entrada, finalidade=4, CFOP 1.x/2.x/3.x)',
                    onClick: async () => {
                      avisoAcao('Gerando rascunho de devolução...');
                      try {
                        const r = await fetch(`/api/nfe/${selecionado.id}/devolucao`, { method: 'POST' });
                        const data = await r.json();
                        if (r.ok && data.id) {
                          avisoAcao(`Rascunho de devolução #${data.id} criado.`);
                          navigate(`/documentos/nfe/${data.id}?devolucao=true`);
                        } else {
                          avisoAcao(`Erro: ${data.error || data.detail || 'falhou'}`);
                        }
                      } catch (e) { avisoAcao('Erro: ' + String(e)); }
                    },
                  },
                  {
                    label: 'Carta de Correção (CC-e)',
                    icon: <FileEdit className="h-3.5 w-3.5" />,
                    hint: 'Envia evento 110110 à SEFAZ. Texto entre 15 e 1000 caracteres.',
                    onClick: () => setCceOpen(true),
                  },
                  {
                    label: 'Baixar última CC-e (PDF)',
                    icon: <Download className="h-3.5 w-3.5" />,
                    hint: 'DACCe da última carta de correção registrada',
                    onClick: () => { window.location.href = `/api/nfe/${selecionado.id}/carta-correcao.pdf`; },
                  },
                  {
                    label: 'Baixar última CC-e (XML)',
                    icon: <FileText className="h-3.5 w-3.5" />,
                    hint: 'procEventoNFe da última carta de correção',
                    onClick: () => { window.location.href = `/api/nfe/${selecionado.id}/carta-correcao.xml`; },
                  },
                ] as DropdownItem[] : []),
...(tipoParam === 'cte' && selecionado.status !== 'autorizada' && selecionado.status !== 'cancelada' ? [{
                  label: 'Reenviar para SEFAZ (criar rascunho)',
                  icon: <Send className="h-3.5 w-3.5" />,
                  hint: 'Cria rascunho a partir desta CT-e rejeitada/denegada para corrigir e retransmitir',
                  onClick: async () => {
                    avisoAcao('Criando rascunho de reenvio...');
                    try {
                      const r = await fetch(`/api/cte/${selecionado.id}/reenviar`, { method: 'POST' });
                      const data = await r.json();
                      if (r.ok && data.id) {
                        avisoAcao(`Rascunho de reenvio #${data.id} criado.`);
                        navigate(`/documentos/cte/${data.id}`);
                      } else avisoAcao(`Erro: ${data.error || data.detail || 'falhou'}`);
                    } catch (e) { avisoAcao('Erro: ' + String(e)); }
                  },
                } as DropdownItem] : []),
                ...(tipoParam === 'nfe' && selecionado.status !== 'autorizada' && selecionado.status !== 'cancelada' ? [{
                  label: 'Corrigir (criar rascunho)',
                  icon: <FileEdit className="h-3.5 w-3.5" />,
                  hint: 'Cria rascunho editável a partir desta NF-e rejeitada/denegada para ajustar e re-emitir',
                  onClick: async () => {
                    avisoAcao('Criando rascunho de correção...');
                    try {
                      const r = await fetch(`/api/nfe/${selecionado.id}/criar-rascunho-correcao`, { method: 'POST' });
                      const data = await r.json();
                      if (r.ok && data.id) {
                        avisoAcao(`Rascunho de correção #${data.id} criado.`);
                        navigate(`/documentos/nfe/${data.id}`);
                      } else avisoAcao(`Erro: ${data.error || data.detail || 'falhou'}`);
                    } catch (e) { avisoAcao('Erro: ' + String(e)); }
                  },
                } as DropdownItem] : []),
                ...(tipoParam === 'nfe' && selecionado.chave ? [
                  {
                    label: 'Baixar XML — só nota',
                    icon: <FileText className="h-3.5 w-3.5" />,
                    hint: 'Sem o protocolo SEFAZ',
                    onClick: () => { window.location.href = `/api/nfe/${selecionado.id}/xml-nota`; },
                  },
                  {
                    label: 'Baixar XML — só protocolo',
                    icon: <FileText className="h-3.5 w-3.5" />,
                    hint: 'Apenas o <protNFe>',
                    onClick: () => { window.location.href = `/api/nfe/${selecionado.id}/xml-protocolo`; },
                  },
                ] as DropdownItem[] : []),
                {
                  label: 'Ver motivo / JSON SEFAZ',
                  icon: <AlertOctagon className="h-3.5 w-3.5" />,
                  hint: 'Mostra retorno_json/json_completo formatado em modal',
                  disabled: !resposta,
                  onClick: () => setJsonOpen(true),
                },
                ...(selecionado.chave ? [{
                  label: 'Compartilhar via WhatsApp',
                  icon: <MessageCircle className="h-3.5 w-3.5" />,
                  hint: 'Abre wa.me com mensagem padronizada (chave + numero)',
                  onClick: () => {
                    const tel = (prompt('Telefone com DDD (ex: 5583999999999):') || '').replace(/\D/g, '');
                    if (!tel) return;
                    const msg = encodeURIComponent(`Segue ${tipoLabel} nº ${selecionado.numero}/${selecionado.serie ?? 1}
Chave: ${selecionado.chave}`);
                    window.open(`https://wa.me/${tel}?text=${msg}`, '_blank');
                  },
                } as DropdownItem] : []),
                ...(tipoParam === 'nfe' && selecionado.chave ? [{
                  label: 'Ver eventos SEFAZ',
                  icon: <Clock className="h-3.5 w-3.5" />,
                  hint: 'Linha do tempo com cancelamento, CC-e e demais eventos desta NF-e',
                  onClick: () => setEventosOpen(true),
                } as DropdownItem] : []),
                {
                  label: 'Copiar chave de acesso',
                  icon: <KeyRound className="h-3.5 w-3.5" />,
                  disabled: !selecionado.chave,
                  hint: selecionado.chave ? 'Copia a chave SEFAZ de 44 dígitos' : 'Documento ainda sem chave',
                  onClick: async () => {
                    const ok = await chaveCopy.copy(String(selecionado.chave ?? ''));
                    avisoAcao(ok ? 'Chave copiada.' : 'Falha ao copiar chave.');
                  },
                },
                {
                  label: 'Copiar número formatado',
                  icon: <Hash className="h-3.5 w-3.5" />,
                  hint: 'Ex.: "Série 1 · Nº 123"',
                  onClick: async () => {
                    const txt = `Série ${selecionado.serie ?? 1} · Nº ${selecionado.numero ?? '?'}`;
                    const ok = await chaveCopy.copy(txt);
                    avisoAcao(ok ? `Copiado: ${txt}` : 'Falha ao copiar.');
                  },
                },
                {
                  label: 'Copiar resposta SEFAZ (JSON)',
                  icon: <Braces className="h-3.5 w-3.5" />,
                  disabled: !resposta,
                  hint: resposta ? 'Copia retorno_json/json_completo formatado' : 'Sem payload SEFAZ disponível',
                  onClick: async () => {
                    if (!resposta) return;
                    const txt = typeof resposta === 'string' ? resposta : JSON.stringify(resposta, null, 2);
                    const ok = await chaveCopy.copy(txt);
                    avisoAcao(ok ? 'Resposta SEFAZ copiada (JSON).' : 'Falha ao copiar.');
                  },
                },
              ];
              return items;
            })() : []}
          />
        </div>

        {aviso && (
          <div className="mt-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            {aviso}
          </div>
        )}
      </section>

      {/* TABELA DE EMITIDOS */}
      <section className="card-surface overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <div>
            <div className="eyebrow">{tipoLabel} emitidos</div>
            <h3 className="text-sm font-semibold text-slate-950 mt-0.5 dark:text-white">Clique em uma linha para selecionar</h3>
          </div>
          <div className="text-xs text-neutral-600 tab-nums dark:text-neutral-400">Página {pg + 1} de {totalPags}</div>
        </div>

        {isLoading && (
          <div className="py-10 grid place-items-center"><LoadingSpinner /></div>
        )}

        {!isLoading && filtrados.length === 0 && (
          <div className="py-10 text-center text-sm text-neutral-600 dark:text-neutral-400">
            Nenhum {tipoLabel} encontrado com os filtros atuais.
          </div>
        )}

        {!isLoading && filtrados.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-[11px] uppercase tracking-[0.08em] text-slate-500 dark:bg-slate-900/80">
                <tr>
                  <th className="px-3 py-2 text-left w-10">Sel</th>
                  <th className="px-3 py-2 text-left">Número</th>
                  <th className="px-3 py-2 text-left">Emissão</th>
                  <th className="px-3 py-2 text-left">Chave de acesso</th>
                  <th className="px-3 py-2 text-left">Rota / Natureza</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {slice.map((d) => {
                  const sel = d.id === selId;
                  return (
                    <tr
                      key={d.id}
                      onClick={() => setSelId(sel ? null : d.id)}
                      className={`cursor-pointer transition ${sel ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-900/70'}`}
                    >
                      <td className="px-3 py-2">
                        <span className={`inline-block h-3.5 w-3.5 rounded-full border-2 ${sel ? 'bg-emerald-400 border-emerald-400' : 'border-slate-300 dark:border-white/30'}`} />
                      </td>
                      <td className="px-3 py-2 font-semibold text-slate-950 dark:text-white">
                        #{d.numero}/{d.serie ?? 1}
                      </td>
                      <td className="px-3 py-2 text-neutral-600 mono dark:text-neutral-300">
                        {formatDate(d.data_emissao)}
                      </td>
                      <td className="px-3 py-2 mono text-xs text-neutral-600 max-w-[14rem] truncate dark:text-neutral-400" title={chaveEmiss(d)}>
                        {chaveEmiss(d) || '—'}
                      </td>
                      <td className="px-3 py-2 text-neutral-600 dark:text-neutral-300">{rotaDoc(d) || '—'}</td>
                      <td className="px-3 py-2 text-right tab-nums font-semibold text-slate-950 dark:text-white">
                        {formatMoney(valorDoc(d))}
                      </td>
                      <td className="px-3 py-2"><StatusBadge status={d.status || 'autorizada'} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPags > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <button
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
              disabled={pg === 0}
              className="toolbar-btn"
            >
              <ChevronLeft className="h-3 w-3" /> Anterior
            </button>
            <span>{pg * PAGE_SIZE + 1}–{Math.min(filtrados.length, (pg + 1) * PAGE_SIZE)} de {filtrados.length}</span>
            <button
              onClick={() => setPagina((p) => Math.min(totalPags - 1, p + 1))}
              disabled={pg === totalPags - 1}
              className="toolbar-btn"
            >
              Próximo <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}

        {selecionado && chaveEmiss(selecionado) && (
          <div className="px-4 py-3 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2 dark:border-slate-800 dark:text-slate-400">
            <FileText className="h-3.5 w-3.5" />
            <span className="mono truncate flex-1">Chave: {chaveEmiss(selecionado)}</span>
            <button
              type="button"
              onClick={() => chaveCopy.copy(chaveEmiss(selecionado))}
              className="btn btn-sm flex items-center gap-1 shrink-0"
              title="Copiar chave para a área de transferência"
            >
              {chaveCopy.copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
              {chaveCopy.copied ? 'Copiada' : 'Copiar'}
            </button>
          </div>
        )}
      </section>

      {/* Rascunhos pendentes do tipo atual */}
      <section className="card-surface overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-white/5">
          <div>
            <div className="eyebrow">Rascunhos pendentes</div>
            <h3 className="text-sm font-semibold text-slate-950 mt-0.5 dark:text-white">
              {tipoLabel} aguardando transmissão ({rascQ.data?.length ?? 0})
            </h3>
          </div>
          <Link to={`/documentos/${tipoParam}/novo`} className="btn-primary text-xs !py-1.5">
            <Plus className="h-3.5 w-3.5" /> Novo {tipoLabel}
          </Link>
        </div>
        {rascQ.isLoading && (
          <div className="py-6 grid place-items-center"><LoadingSpinner /></div>
        )}
        {!rascQ.isLoading && (rascQ.data?.length ?? 0) === 0 && (
          <p className="py-6 text-center text-sm text-neutral-500">
            Nenhum rascunho pendente.
          </p>
        )}
        {!rascQ.isLoading && (rascQ.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-slate-200 dark:border-white/10">
                <tr>
                  <th className="py-2 px-3">Criado</th>
                  <th className="py-2 px-3">Nº proposto</th>
                  <th className="py-2 px-3">Série</th>
                  <th className="py-2 px-3">Motivo</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {rascQ.data!.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                    <td className="py-2 px-3 text-xs text-neutral-600 whitespace-nowrap dark:text-neutral-400">
                      {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="py-2 px-3 mono">{r.numero_proposto ?? '—'}</td>
                    <td className="py-2 px-3">{r.serie ?? '—'}</td>
                    <td className="py-2 px-3 max-w-md truncate text-neutral-600 dark:text-neutral-400" title={r.motivo || ''}>
                      {r.motivo || '—'}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <div className="flex gap-1 justify-end">
                        <Link
                          to={`/documentos/${tipoParam}/${r.id}`}
                          className="btn-sky text-[10px] !py-0.5 !px-1.5"
                        >
                          <Pencil className="h-3 w-3" /> Editar
                        </Link>
                        {tipoParam === 'nfe' && (
                          <a
                            href={`/api/nfe/rascunhos/${r.id}/previa.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="toolbar-btn text-[10px] !py-0.5 !px-1.5"
                            title="Visualizar prévia do rascunho (sem valor fiscal)"
                          >
                            <FileText className="h-3 w-3" /> Prévia
                          </a>
                        )}
                        <button
                          type="button"
                          className="btn-danger text-[10px] !py-0.5 !px-1.5"
                          onClick={async () => {
                            if (!confirm(`Excluir rascunho #${r.id}?`)) return;
                            const tipoApi = tipoParam === 'cte-os' ? 'cte-os' : tipoParam;
                            try {
                              const res = await fetch(`/api/${tipoApi}/rascunhos/${r.id}`, { method: 'DELETE' });
                              if (res.ok) { rascQ.refetch(); avisoAcao(`Rascunho #${r.id} excluído.`); }
                              else avisoAcao('Erro ao excluir.');
                            } catch (e) { avisoAcao('Erro: ' + String(e)); }
                          }}
                        >
                          <Trash2 className="h-3 w-3" /> Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* RODAPÉ: acessos rápidos a pessoas/produtos */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Link to={`/pessoas?emitente=${emitenteId}`} className="card-surface p-4 flex items-center gap-3 hover:border-sky-500/30 transition">
          <UsersIcon className="h-5 w-5 text-sky-700 dark:text-sky-400" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-950 dark:text-white">Clientes / Destinatários</div>
            <div className="text-xs text-neutral-600 dark:text-neutral-400">Cadastro unificado de pessoas</div>
          </div>
        </Link>
        <Link to={`/produtos?emitente=${emitenteId}`} className="card-surface p-4 flex items-center gap-3 hover:border-amber-500/30 transition">
          <Truck className="h-5 w-5 text-amber-400" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-950 dark:text-white">Produtos / Estoque</div>
            <div className="text-xs text-neutral-600 dark:text-neutral-400">Cadastro unificado</div>
          </div>
        </Link>
        <Link to={`/configuracoes`} className="card-surface p-4 flex items-center gap-3 hover:border-violet-500/30 transition">
          <Radio className="h-5 w-5 text-violet-400" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-950 dark:text-white">SMTP / WhatsApp</div>
            <div className="text-xs text-neutral-600 dark:text-neutral-400">Configurações de envio</div>
          </div>
        </Link>
      </section>

      {selecionado && (
        <ConfirmacaoModal
          open={cceOpen}
          titulo={`Carta de Correção — NF-e nº ${selecionado.numero ?? '?'}`}
          mensagem={
            <>
              <strong>Atenção:</strong> a CC-e é transmitida imediatamente à SEFAZ (evento 110110) e ficará vinculada à NF-e. Não pode corrigir valor, alíquota, base de cálculo, destinatário ou data. Máximo 20 CC-e por nota.
              {selecionado.chave && (
                <div className="mt-2 text-xs mono break-all opacity-80">{selecionado.chave}</div>
              )}
            </>
          }
          textareaLabel="Texto da correção"
          textareaPlaceholder="Ex.: 'Endereço do destinatário corrigido para Rua X, 100.' (mínimo 15, máximo 1000 caracteres)"
          textareaMinChars={15}
          textareaMaxChars={1000}
          tom="info"
          confirmLabel="Enviar CC-e"
          cancelLabel="Voltar"
          onCancel={() => setCceOpen(false)}
          onConfirm={async (correcao) => {
            try {
              const r = await fetch(`/api/nfe/${selecionado.id}/carta-correcao`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ correcao }),
              });
              const data = await r.json();
              if (data.sucesso) {
                emitQ.refetch();
                return {
                  sucesso: true,
                  mensagem: `CC-e nº ${data.evento_id ?? '—'} registrada (cStat ${data.cStat}${data.nProt ? ' · prot ' + data.nProt : ''}): ${data.xMotivo}`,
                };
              }
              return {
                sucesso: false,
                mensagem: `cStat ${data.cStat ?? '-'}: ${data.xMotivo ?? data.error ?? data.detail ?? 'SEFAZ rejeitou a CC-e.'}`,
              };
            } catch (e) {
              return { sucesso: false, mensagem: 'Erro: ' + String(e) };
            }
          }}
        />
      )}

      <EnviarEmailModal
        open={emailOpen}
        tipo={tipoParam}
        tipoLabel={tipoLabel}
        doc={selecionado}
        emailEmitente={empresaQ.data?.email_envio_notas}
        onClose={() => setEmailOpen(false)}
        onEnviado={(para) => avisoAcao(`E-mail enviado para ${para.join(', ')}.`)}
      />

      <EventosNfeModal
        open={eventosOpen}
        nfeId={tipoParam === 'nfe' && selecionado ? selecionado.id : null}
        nfeNumero={tipoParam === 'nfe' && selecionado ? (selecionado.numero ?? null) : null}
        onClose={() => setEventosOpen(false)}
      />

      <JsonViewerModal
        open={jsonOpen}
        titulo={selecionado ? `Resposta SEFAZ — ${tipoLabel} #${selecionado.numero}` : 'Resposta SEFAZ'}
        data={selecionado ? ((selecionado as DocEmitido & { retorno_json?: unknown; json_completo?: unknown }).retorno_json ?? (selecionado as DocEmitido & { retorno_json?: unknown; json_completo?: unknown }).json_completo) : null}
        onClose={() => setJsonOpen(false)}
      />
    </div>
  );
}
